import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ContentApi } from "../../../../shared/api/contentApi";

import GroupParameterEditDialog from "../components/EditGroupParameter.jsx";

import DetailPageHero from "../../../../components/ui/DetailPageHero";
import DetailTabs, { DetailTabsContent } from "../../../../components/ui/DetailTabs";
import DetailPageSidebar from "../../../../components/ui/DetailPageSidebar";
import { Info, Database } from "lucide-react";
import { useContentContext } from "../../ContentContext";
import { notify } from "shared/utils/notify";
import formatDate from "../../../../shared/helpers/formatDate";
import { Flex, Select, Button, Modal, Input, Card, Checkbox, Splitter } from "antd";
import SystemEditGroup from "../components/SystemEditGroup";
import { DETAIL_CARD_CLASS, DETAIL_FIELD_CLASS } from "../../../../shared/constants/detailLayout";

// Helper component for JSON display
function Jsonish({ value }) {
    if (value === null || value === undefined) return <span className="text-zinc-400">null</span>;
    if (typeof value === "string") return <span className="text-green-600">"{value}"</span>;
    if (typeof value === "number") return <span className="text-blue-600">{value}</span>;
    if (typeof value === "boolean")
        return <span className="text-purple-600">{value.toString()}</span>;
    return <pre className="text-xs">{JSON.stringify(value, null, 2)}</pre>;
}

export default function GroupParameterDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { canMutate, selectedVersion, projectId, versionId } = useContentContext();
    const [groupParameter, setGroupParameter] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [editOpen, setEditOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [allParameters, setAllParameters] = useState([]);
    const [groupParameterLinks, setGroupParameterLinks] = useState([]);
    const [attachParameterId, setAttachParameterId] = useState(undefined);
    const [attachingParameter, setAttachingParameter] = useState(false);
    const [linkDrafts, setLinkDrafts] = useState({});
    const [savingLinkIds, setSavingLinkIds] = useState({});
    const [detachingLinkIds, setDetachingLinkIds] = useState({});

    const loadDetails = async (signal) => {
        if (!projectId || !versionId || !id) {
            setGroupParameter(null);
            setAllParameters([]);
            setGroupParameterLinks([]);
            setAttachParameterId(undefined);
            setLinkDrafts({});
            setLoading(false);
            return;
        }

        setLoading(true);
        setError("");
        try {
            const [groupParameterData, parametersOfGroupList, allParametersPage] = await Promise.all([
                ContentApi.getParameterGroupById(projectId, versionId, id, { signal }),
                ContentApi.getParameterGroupParameters(projectId, versionId, id, { signal }),
                ContentApi.getParametersPaged({ projectId, versionId, page: 1, pageSize: 1000 }, { signal }),
            ]);
            setGroupParameter(groupParameterData || null);
            // const links = Array.isArray(parametersOfGroupList?.items) ? parametersOfGroupList.items : [];
            const links = []
            if (parametersOfGroupList?.items) {
                links.push(...parametersOfGroupList.items);
            }
            else if (Array.isArray(parametersOfGroupList)) {
                links.push(...parametersOfGroupList);
            }
            setGroupParameterLinks(links);
            setAllParameters(Array.isArray(allParametersPage?.items) ? allParametersPage.items : []);
            setLinkDrafts(
                links.reduce((acc, link) => {
                    if (!link?.id) return acc;
                    acc[link.id] = {
                        isRequired: Boolean(link?.isRequired),
                        orderIndex: link?.orderIndex ?? "",
                    };
                    return acc;
                }, {})
            );
        } catch (e) {
            if (!signal?.aborted) {
                setError(e?.message || "Ошибка загрузки");
            }
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    };

    useEffect(() => {
        const ac = new AbortController();
        loadDetails(ac.signal);

        return () => ac.abort();
    }, [id, projectId, versionId]);

    const handleAttachToGroup = async () => {
        if (!canMutate || !attachParameterId || !id) return;

        setAttachingParameter(true);
        try {
            await ContentApi.attachParameterToGroup(projectId, versionId, id, { parameterId: attachParameterId });
            setAttachParameterId(undefined);
            notify.success("Параметр присоединён к группе");
            await loadDetails();
        } catch (err) {
            notify.error(err?.message || "Не удалось присоединить параметр к группе");
        } finally {
            setAttachingParameter(false);
        }
    };

    const handleDelete = async () => {
        try {
            await ContentApi.deleteParameterGroup(projectId, versionId, id);
            notify.success("Параметр удалён");
            navigate("/content/group-parameters");
        } catch (err) {
            notify.error(`Ошибка удаления: ${err.message}`);
        }
    };

    const handleLinkDraftChange = (linkId, patch) => {
        setLinkDrafts((prev) => ({
            ...prev,
            [linkId]: {
                ...(prev[linkId] || {}),
                ...patch,
            },
        }));
    };

    const handleSaveLink = async (link) => {
        const linkId = link?.id;
        if (!canMutate || !id || !linkId) return;

        const draft = linkDrafts[linkId] || {
            isRequired: Boolean(link?.isRequired),
            orderIndex: link?.orderIndex ?? "",
        };
        const parsedOrderIndex = Number.parseInt(String(draft.orderIndex), 10);
        const payload = {
            isRequired: Boolean(draft.isRequired),
        };
        if (Number.isFinite(parsedOrderIndex)) {
            payload.orderIndex = parsedOrderIndex;
        }

        setSavingLinkIds((prev) => ({ ...prev, [linkId]: true }));
        try {
            await ContentApi.patchParameterGroupLink(projectId, versionId, id, linkId, payload);
            notify.success("Привязка параметра обновлена");
            await loadDetails();
        } catch (err) {
            notify.error(err?.message || "Не удалось обновить привязку параметра");
        } finally {
            setSavingLinkIds((prev) => ({ ...prev, [linkId]: false }));
        }
    };

    const handleDetachLink = async (linkId) => {
        if (!canMutate || !id || !linkId) return;

        setDetachingLinkIds((prev) => ({ ...prev, [linkId]: true }));
        try {
            await ContentApi.detachParameterFromGroup(projectId, versionId, id, linkId);
            notify.success("Параметр отвязан от группы");
            await loadDetails();
        } catch (err) {
            notify.error(err?.message || "Не удалось отвязать параметр от группы");
        } finally {
            setDetachingLinkIds((prev) => ({ ...prev, [linkId]: false }));
        }
    };

    const tabs = [
        { value: "overview", label: "Обзор", icon: <Info size={16} /> },
        { value: "usage", label: "Использование", icon: <Database size={16} /> },
        { value: "meta", label: "Метаданные", icon: <Info size={16} /> },
    ];

    const autoApplyEntities = (() => {
        const rawValue = groupParameter?.autoApplyForEntityTypes ?? groupParameter?.autoApplyForEntityTypesJson;

        if (Array.isArray(rawValue)) {
            return rawValue.filter(Boolean);
        }

        if (typeof rawValue === "string") {
            const trimmed = rawValue.trim();
            if (!trimmed) return [];

            try {
                const parsed = JSON.parse(trimmed);
                return Array.isArray(parsed) ? parsed.filter(Boolean) : [trimmed];
            } catch {
                return trimmed.startsWith("[")
                    ? [trimmed]
                    : trimmed.split(",").map((item) => item.trim()).filter(Boolean);
            }
        }

        return [];
    })();

    const attachedParameterIds = new Set([
        ...groupParameterLinks
            .map((link) => link?.parameterId || link?.parameter?.id)
            .filter(Boolean),
        ...(groupParameter?.parameterIds || []).filter(Boolean),
    ]);

    const availableParameters = allParameters.filter((parameter) => !attachedParameterIds.has(parameter.id));

    if (loading) {
        return (
            <section className="p-4 md:p-6">
                <div className="p-6 text-sm text-zinc-500">Загрузка…</div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="p-4 md:p-6">
                <div className="rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                    {error}
                </div>
            </section>
        );
    }

    if (!groupParameter) {
        return (
            <section className="p-4 md:p-6">
                <div className="p-6 text-sm text-zinc-500">Не найдено</div>
            </section>
        );
    }

    return (
        <section className="p-4 md:p-6">
            <div className="mx-auto w-full max-w-6xl">
                <Flex vertical gap="large" className="mt-2 w-full">
                    <div className="w-full">
                        <DetailTabs defaultValue="overview" tabs={tabs} onDelete={canMutate ? () => setDeleteDialogOpen(true) : undefined}>
                            {/* Overview Tab */}
                            <DetailTabsContent value="overview" className="space-y-4">
                                <DetailPageHero
                                    title={groupParameter.name}
                                    subtitle={groupParameter.systemName}
                                    breadcrumbs={[
                                        { label: "Групповые параметры", to: "/content/group-parameters" },
                                        { label: "Детали" },
                                    ]}
                                    metadata={[
                                        { label: "ID", value: `#${groupParameter.id || groupParameter.groupParameterId}` },
                                        { label: "Закреплено", value: `${groupParameter.isLocked ? "Да" : "Нет"}` },
                                    ]}
                                />

                                {!canMutate && (
                                    <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-700 p-3 text-sm mb-4">
                                        Версия {selectedVersion?.status || "unknown"} только для чтения. Используйте Fork в draft для изменений.
                                    </div>
                                )}
                                <SystemEditGroup
                                    parameter={groupParameter}
                                    projectId={projectId}
                                    versionId={versionId}
                                    onClose={() => setEditOpen(false)}
                                    onSaved={async (updated) => {
                                        setGroupParameter(updated || groupParameter);
                                        await loadDetails();
                                        setEditOpen(false);
                                    }}
                                />

                                <Card title="Дополнительно" className={DETAIL_CARD_CLASS}>
                                    <div className=" grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-zinc-500">
                                        <div>Создана: {formatDate(groupParameter.createdAt) ?? "-"}</div>
                                        <div>
                                            Обновлено: {groupParameter.updatedAt ? formatDate(groupParameter.updatedAt) : formatDate(groupParameter.createdAt)}
                                        </div>
                                    </div>
                                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-zinc-500">
                                        <div>Создана кем: {groupParameter.createdBy ?? "-"}</div>
                                        <div>
                                            Обновлено кем: {groupParameter.updatedBy ?? "-"}
                                        </div>
                                    </div>
                                </Card>
                                <Card title="Автоприменение к сущностям" className={DETAIL_CARD_CLASS}>
                                    <h2 className="eyebrow mb-2">Автоприменение к сущностям</h2>
                                    {autoApplyEntities.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {autoApplyEntities.map((entity) => (
                                                <span key={entity} className="chip">
                                                    {entity}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-zinc-500">Нет сущностей для автоприменения</p>
                                    )}
                                </Card>
                            </DetailTabsContent>

                            <DetailTabsContent value="usage">
                                <Card title="Использование параметров" className={DETAIL_CARD_CLASS}>
                                    <p className="text-sm text-zinc-500 mb-3">
                                        Присоедините параметры к группе параметров.
                                    </p>
                                    <Flex gap="small" align="center" wrap>
                                        <Select
                                            className={DETAIL_FIELD_CLASS}
                                            value={attachParameterId}
                                            onChange={setAttachParameterId}
                                            placeholder={availableParameters.length ? "Выберите параметр" : "Нет доступных параметров"}
                                            disabled={!canMutate || attachingParameter || availableParameters.length === 0}
                                            options={availableParameters.map((parameter) => ({
                                                label: parameter.name || parameter.systemName || parameter.id,
                                                value: parameter.id,
                                            }))}
                                        />
                                        <Button
                                            type="primary"
                                            onClick={handleAttachToGroup}
                                            loading={attachingParameter}
                                            disabled={!canMutate || attachingParameter || !attachParameterId}
                                        >
                                            Присоединить
                                        </Button>
                                    </Flex>
                                    <div className="mt-4 border rounded-xl overflow-hidden">
                                        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 px-3 py-2 bg-zinc-50 border-b text-xs font-medium text-zinc-600">
                                            <div className="md:col-span-2">Параметр</div>
                                            <div>Обязательный</div>
                                            <div>Порядок</div>
                                            <div className="md:text-right">Действия</div>
                                        </div>

                                        {groupParameterLinks.length === 0 ? (
                                            <div className="px-3 py-4 text-sm text-zinc-500">В группе пока нет параметров.</div>
                                        ) : (
                                            <div className="divide-y">
                                                {groupParameterLinks.map((link) => {
                                                    const parameter = link?.parameter || allParameters.find((item) => item.id === link?.parameterId);
                                                    const draft = linkDrafts[link.id] || {
                                                        isRequired: Boolean(link?.isRequired),
                                                        orderIndex: link?.orderIndex ?? "",
                                                    };
                                                    const isSaving = Boolean(savingLinkIds[link.id]);
                                                    const isDetaching = Boolean(detachingLinkIds[link.id]);

                                                    return (
                                                        <div key={link.id} className="grid grid-cols-1 md:grid-cols-5 gap-2 px-3 py-3 items-center">
                                                            <div className="md:col-span-2 min-w-0">
                                                                <div className="text-sm truncate">{parameter?.name || parameter?.systemName || link?.parameterId}</div>
                                                                <div className="text-xs text-zinc-500 truncate">{parameter?.systemName || link?.parameterId}</div>
                                                            </div>

                                                            <label className="inline-flex items-center gap-2 text-sm">
                                                                <Checkbox
                                                                    checked={Boolean(draft.isRequired)}
                                                                    onChange={(e) => handleLinkDraftChange(link.id, { isRequired: e.target.checked })}
                                                                    disabled={!canMutate || isSaving || isDetaching}
                                                                />
                                                                Да
                                                            </label>

                                                            <Input
                                                                type="number"
                                                                className="input w-24"
                                                                value={draft.orderIndex}
                                                                onChange={(e) => handleLinkDraftChange(link.id, { orderIndex: e.target.value })}
                                                                disabled={!canMutate || isSaving || isDetaching}
                                                            />

                                                            <div className="flex items-center gap-2 md:justify-end">
                                                                <Button
                                                                    type="primary"
                                                                    loading={isSaving}
                                                                    disabled={!canMutate || isSaving || isDetaching}
                                                                    onClick={() => handleSaveLink(link)}
                                                                >
                                                                    Сохранить
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    color="danger"
                                                                    loading={isDetaching}
                                                                    disabled={!canMutate || isSaving || isDetaching}
                                                                    onClick={() => handleDetachLink(link.id)}
                                                                >
                                                                    Удалить
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </Card>
                            </DetailTabsContent>


                            <DetailTabsContent value="meta">
                                <Card title="Метаданные" className={DETAIL_CARD_CLASS}>
                                    <dl className="space-y-2 text-sm">
                                        <div>
                                            <dt className="eyebrow">ID</dt>
                                            <dd className="text-xs">{groupParameter?.id || groupParameter?.groupParameterId}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">Group Parameter ID</dt>
                                            <dd className="text-xs">{groupParameter?.groupParameterId || '-'}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">ValueTypeId</dt>
                                            <dd className="text-xs">{groupParameter?.valueTypeId || '-'}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">OrderKey</dt>
                                            <dd className="text-xs">{groupParameter?.orderKey || '-'}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">Is locked</dt>
                                            <dd className="text-xs">{groupParameter?.isLocked || '-'}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">Name Id</dt>
                                            <dd className="text-xs">{groupParameter?.nameTrId || '-'}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">Description Id</dt>
                                            <dd className="text-xs">{groupParameter?.descriptionTrId || '-'}</dd>
                                        </div>
                                    </dl>
                                </Card>
                            </DetailTabsContent>
                        </DetailTabs>
                    </div>
                </Flex>

                {/* Edit Dialog */}
                <GroupParameterEditDialog
                    open={editOpen}
                    parameter={groupParameter}
                    projectId={projectId}
                    versionId={versionId}
                    onClose={() => setEditOpen(false)}
                    onSaved={async (updated) => {
                        setGroupParameter(updated || groupParameter);
                        await loadDetails();
                        setEditOpen(false);
                    }}
                />

                {/* Delete Confirmation Dialog */}
                <Modal
                    open={deleteDialogOpen}
                    onCancel={() => setDeleteDialogOpen(false)}
                    title="Удалить группу параметров?"
                    closable={false}
                    centered
                    width=""
                    className=""
                    styles={{
                        body: { padding: 0 },
                        content: { overflow: "hidden" },
                    }}
                    onOk={() => { setDeleteDialogOpen(false); handleDelete(); }}
                    footer={(_, { OkBtn, CancelBtn }) => (
                        <>
                            <OkBtn />
                            <CancelBtn />
                        </>
                    )}
                >
                    <div className="text-sm text-zinc-600 dark:text-zinc-400 mb-2">
                        Внимание! Удаление группы параметров может привести к ошибкам, которые их используют.
                    </div>
                    <p>
                        <strong>Группа параметров:</strong> {groupParameter?.name}
                    </p>
                    <p className="mt-1">
                        <strong>System Name:</strong> {groupParameter?.systemName}
                    </p>
                </Modal>
            </div>
        </section>
    );
}